import { defineStore } from "pinia";
import {
  UserNotification,
  UserNotificationsState,
} from "../types/user-notifications-interface";
import notificationsApi from "../services/notification-service";
import {
  createNotificationFromDto,
  createPaginationFromDto,
  NotificationDto,
  PaginationDto,
} from "../types/notification-request-payload";
import { useChartGroupsStore } from "@/features/chart-groups/store/chart-groups-store";

export const useUserNotificationsStore = defineStore("userNotificationsStore", {
  state: (): UserNotificationsState => {
    return {
      isLoading: false,
      notifications: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
      },
    };
  },
  getters: {
    getIsLoading(): boolean {
      return this.isLoading;
    },
    getNotifications(): UserNotification[] {
      return this.notifications;
    },
    getPagination(): { currentPage: number; totalPages: number } {
      return this.pagination;
    },
    getChartGroupId(): number | null {
      return useChartGroupsStore().getSelectedChartGroupId;
    },
  },
  actions: {
    async fetchNotifications(chartGroupId: number) {
      try {
        this.isLoading = true;
        const result = await notificationsApi.fetchNotifications(chartGroupId);
        this.setNotifications(result.data.payload);
        this.setPagination(result.data.pagination);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    markNotificationAsRead(chartGroupId: number, notificationId: number) {
      this.setNotificationAsRead(notificationId);
      notificationsApi.markNotificationAsRead(chartGroupId, notificationId);
    },
    async fetchAdditionalNotifications(chartGroupId?: number, page?: number) {
      if (this.isLoading) return;

      const nextPage = page || this.pagination.currentPage + 1;
      if (nextPage > this.pagination.totalPages) return;

      try {
        this.isLoading = true;

        if (!chartGroupId) return;
        const result = await notificationsApi.fetchAdditionalNotifications(
          chartGroupId,
          nextPage
        );

        if (result) {
          this.appendNotifications(result.data.payload);
          this.setPagination(result.data.pagination);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    setNotifications(notificationResponseData: NotificationDto) {
      const notifications = createNotificationFromDto(notificationResponseData);
      this.notifications = notifications;
    },
    setNotificationAsRead(notificationId: number) {
      const notification = this.notifications.find(
        (notification) => notification.id === notificationId
      );
      if (notification) {
        notification.read = true;
      }
    },
    setPagination(paginationData: PaginationDto) {
      const pagination = createPaginationFromDto(paginationData);
      this.pagination = pagination;
    },
    appendNotifications(notificationResponseData: NotificationDto) {
      const notifications = createNotificationFromDto(notificationResponseData);
      this.notifications.push(...notifications);
    },
  },
});
